import { BookRedia, GameRedia, VideoRedia } from "@biblioteksentralen/icons";
import { Box, BoxProps, ChakraProps, Flex, Grid, Image, colors } from "@biblioteksentralen/react";
import { Music } from "react-feather";
import { useSitePalette } from "../../utils/useSitePalette";
import { FavoriteButtonWrapper } from "./favorites/FavoriteButton";
import {
  chooseCoverImageQuality,
  LCWork,
  CoverImageQuality,
  LCWorkTriple,
  isLCWork,
  isLCWorkTriple,
} from "@libry-content/integrations";
import { isPublication, Publication } from "@libry-content/types";
import { SanityImagePreservingAspectRatio } from "../../components/images/SanityImagePreservingAspectRatio";
import { coverImageAltText } from "../../utils/coverImageAltText";

export type CoverImageItem = LCWork | LCWorkTriple | Publication | string;

interface Props {
  item?: CoverImageItem;
  quality?: CoverImageQuality;
  usePlaceholder?: boolean;
  showFavoriteButton?: boolean;
  resolution?: number;
  hoverEffect?: boolean;
}

const getCommonStyling = (hoverEffect: boolean): ChakraProps => ({
  boxShadow: "md",
  borderRadius: "lg",
  _groupHover: hoverEffect ? { transition: ".5s", transform: "scale(1.05)" } : {},
  transition: hoverEffect ? "0.9s" : "",
});

export const ItemCoverImage = ({
  item,
  quality,
  usePlaceholder,
  showFavoriteButton,
  resolution,
  hoverEffect = true,
  ...boxProps
}: Props & BoxProps) => {
  const itemImage = getItemImage(item);
  const commonStyling = getCommonStyling(hoverEffect);
  const palette = useSitePalette();
  const workOrPublication = getItemWorkOrPublication(item);

  if (!item) return null;

  const src = typeof itemImage === "string" ? chooseCoverImageQuality(itemImage, quality ?? "thumbnail") : undefined;
  const blurSrc = typeof itemImage === "string" ? chooseCoverImageQuality(itemImage, "blur") : undefined;

  const alt = getItemAltText(item);
  const Icon = getPlaceholderIcon(item);

  if (usePlaceholder || !itemImage)
    return (
      <Box height="fit-content" position={showFavoriteButton ? "relative" : undefined} {...boxProps}>
        <Grid
          {...commonStyling}
          css="aspect-ratio: 0.7;"
          background={palette.colors.lightaccent1.css.background}
          color={colors.white}
          placeItems="center"
          aria-hidden
        >
          {Icon && <Icon fontSize="3rem" width="60%" height="" />}
        </Grid>
        {showFavoriteButton && (
          <FavoriteButtonWrapper
            workOrPublication={workOrPublication}
            position="absolute"
            bottom=".4rem"
            left=".4rem"
          />
        )}
      </Box>
    );

  return (
    <Flex height="fit-content" position="relative" {...boxProps}>
      {src && blurSrc && (
        <Image
          fallback={
            // Egen komponent for å få blur på fallbackbilde. Hvis vi ikke trenger blur kan vi bruke fallbackSrc={blurSrc} istedenfor
            <Image
              {...commonStyling}
              src={blurSrc}
              width="100%"
              height="auto"
              filter="blur(.15rem)"
              alt={alt}
              display="block"
            />
          }
          {...commonStyling}
          src={src}
          alt={alt}
          display="block"
          height="auto"
          width="100%"
        />
      )}
      {isPublication(item) && (
        <SanityImagePreservingAspectRatio
          image={item.image}
          alt={coverImageAltText(item)}
          hideCaption
          resolution={resolution ?? 250}
          {...commonStyling}
        />
      )}
      {showFavoriteButton && (
        <FavoriteButtonWrapper workOrPublication={workOrPublication} position="absolute" bottom=".4rem" left=".4rem" />
      )}
    </Flex>
  );
};

const getItemAltText = (item?: CoverImageItem) => {
  if (isLCWork(item)) return item.formattedTitle;
  if (isLCWorkTriple(item)) return item.work.formattedTitle;
  if (isPublication(item)) return item.title;
};

const getItemWorkOrPublication = (item?: CoverImageItem) => {
  if (isLCWork(item)) return item;
  if (isLCWorkTriple(item)) return item.work;
  if (isPublication(item)) return item;
};

const getItemImage = (item?: CoverImageItem) => {
  if (typeof item === "string") return item;
  if (isLCWork(item)) return item.getWorkCoverImage();
  if (isLCWorkTriple(item)) return item.coverImageUrl;
  if (isPublication(item)) return item.image;
};

export const getPlaceholderIcon = (item?: CoverImageItem) => {
  if (isLCWork(item)) return item.placeholderIcon;
  if (isLCWorkTriple(item)) return item.work.placeholderIcon;
  if (isPublication(item)) return getPlaceholderIconFromPublication(item);
  return BookRedia;
};

const getPlaceholderIconFromPublication = ({ workType }: Publication) => {
  if (workType === "Musical work") return Music;
  if (workType === "Moving image work") return VideoRedia;
  if (workType === "Interactive work") return GameRedia;
  return BookRedia;
};
